import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { BillOut } from "../../../../../api/bills";
import type { Reservation } from "../../../../../api/reservations";
import type { SchemaRegion } from "../../../../../api/restaurantSchema";
import { TAKEN_TABLES_KEY, useTakenTables } from "../../../../../hooks/useBills";
import { useRestaurantSchema } from "../../../../../hooks/useRestaurantSchema";
import { useIsEdgeLocal, useReservationsLocked } from "../../../../../hooks/useEdgeMode";
import { useAppSelector } from "../../../../../store/hooks";
import {
  createFloorSnapshot,
  reservationBillIds,
  reservationStartMs,
  tableNameKey,
  type FloorTable,
} from "../../../../reservations/logic/tableStatus";
import { deriveReservations } from "../../../../reservations/logic/settleClosedBills";
import { useNow } from "../../../../reservations/hooks/useNow";
import { SEATABLE_KEY, useSeatableReservations } from "../seat/useSeatReservations";
import {
  billShownForWaiter,
  billsAsOpenTables,
  billTableNames,
  discountMarkOf,
  hiddenOpenTableFor,
  indexSchemaTables,
  isClosedEmptyBill,
  mergeOpenTables,
  nonFiscalBills,
  takenTablesAsOpenTables,
  temporaryBills,
  withBillTables,
} from "./orderingFloorData";

const NO_REGIONS: SchemaRegion[] = [];

/**
 * Данните на залата в режим „Схема" — СЪЩАТА снимка като в „Резервации"
 * (`createFloorSnapshot` в момента „сега"):
 * - схемата (`GET /restaurant-schema`, само в режима, без интервал — четенето
 *   е в одита);
 * - заетите маси: своите сметки от списъка + чуждите невидими (без
 *   `view_all_users_bills`) — от „Настани" (`GET /reservations/seatable`,
 *   право `seat_reservations`), а без него при обща номерация от
 *   `GET /bills/taken-tables` (само масата и сервитьорът; никога и двете),
 *   за да не изглеждат масите на колегите свободни;
 * - резервациите (предстоящи и настанени) — от същата заявка; без правото или
 *   срещу каса без резервации — без тях (само заетите маси);
 * - номерацията на масите: при ОБЩА чуждите заети маси са бледи (извън
 *   фокуса — `isOtherWaitersTable`), но се избират като своите; при ЛИЧНА
 *   залата е само на показания сервитьор (чуждите не заемат маса — може да
 *   има две „1"). Резервациите се сверяват с ВСИЧКИ отворени сметки
 *   (`allTableBills`), иначе настанените на колегите биха „приключили".
 * Без интервал: опреснява се при клик по маса (`refreshFloor`), както
 * сметките.
 */
export const useOrderingFloor = ({
  enabled,
  tableBills,
  allTableBills,
  sortedBills,
  closingBillIds,
  sharedNumbering,
  focusWaiterId,
}: {
  enabled: boolean;
  /** Показаните сметки (вкл. закриващите се) — за заетите маси (закритите
   *  без бон не заемат маса); при лична номерация — само на показания. */
  tableBills: BillOut[];
  /** Всички сметки на списъка — за сверката на резервациите. */
  allTableBills: BillOut[];
  /** Подредени като в списъка (само на показания сервитьор) — за двете
   *  ленти: „Временни маси" и „Нефискализирани маси". */
  sortedBills: BillOut[];
  closingBillIds: ReadonlySet<number>;
  /** Обща номерация на масите (настройка на ресторанта). */
  sharedNumbering: boolean;
  /** Сервитьорът на фокус (полето „Сервитьор" или влезлият). */
  focusWaiterId: number | null;
}) => {
  const queryClient = useQueryClient();
  const permissions = useAppSelector((state) => state.auth.permissions);
  const canSeat = permissions.some((permission) => permission.name === "seat_reservations");
  const seesAllBills = permissions.some((permission) => permission.name === "view_all_users_bills");
  const reservationsLocked = useReservationsLocked();
  const edgeLocal = useIsEdgeLocal();
  const readsReservations = enabled && canSeat && !reservationsLocked;
  // Невидимите чужди маси — само ако „Настани" не ги носи; при лична номерация
  // чуждите не заемат маса, а с `view_all_users_bills` списъкът вижда всички.
  // Касата в локален режим не сервира пътя — там (както без „Настани" и
  // досега) чуждите не се виждат.
  const readsTakenTables =
    enabled && sharedNumbering && !seesAllBills && !readsReservations && !edgeLocal;

  const schemaQuery = useRestaurantSchema({ enabled, refetchOnFocus: false });
  const seatableQuery = useSeatableReservations({ enabled: readsReservations, poll: false });
  const seatable = readsReservations ? seatableQuery.data : undefined;
  const takenTablesQuery = useTakenTables(readsTakenTables);
  const takenTables = readsTakenTables ? takenTablesQuery.data : undefined;
  // Тиктака само в режима — иначе целият `Ordering` би се прерисувал всяка минута.
  const nowMs = useNow(enabled);

  const regions = schemaQuery.data ?? NO_REGIONS;
  const index = useMemo(() => indexSchemaTables(regions), [regions]);
  const schemaTables = useMemo(
    () =>
      regions.flatMap((region) =>
        region.elements
          .filter((element) => element.kind === "table")
          .map((table) => ({ id: table.id, name: table.name ?? "" })),
      ),
    [regions],
  );

  /** Масите, които сметката държи сама (при „Една сметка" за резервация на
   *  няколко маси — всичките ѝ; смята ги BE). В локален режим резервацията по
   *  сметката е остаряло копие — само името. */
  const tableNamesOfBill = useCallback(
    (bill: BillOut) => (edgeLocal ? [bill.name] : billTableNames(bill)),
    [edgeLocal],
  );

  // Невидимите чужди — от „Настани" или от `GET /bills/taken-tables`.
  const hiddenSource = useMemo(
    () => seatable?.openTables ?? takenTablesAsOpenTables(takenTables ?? []),
    [seatable, takenTables],
  );
  // Всички отворени (+ невидимите чужди) — за резервациите.
  const settleOpenTables = useMemo(
    () => mergeOpenTables(billsAsOpenTables(allTableBills, closingBillIds), hiddenSource),
    [allTableBills, closingBillIds, hiddenSource],
  );
  // Заетите маси на залата: при обща номерация — всички; при лична — само
  // показаните (чуждата „1" не заема моята „1").
  const openTables = useMemo(
    () => (sharedNumbering ? settleOpenTables : billsAsOpenTables(tableBills, closingBillIds)),
    [sharedNumbering, settleOpenTables, tableBills, closingBillIds],
  );
  // „Една сметка" за резервация на няколко маси заема всичките си маси (и
  // невидимата чужда — масите ѝ идват с `GET /bills/taken-tables`).
  const floorOpenTables = useMemo(() => {
    const billsById = new Map(allTableBills.map((bill) => [bill.id, bill]));
    const takenNamesById = new Map((takenTables ?? []).map((taken) => [taken.billId, taken.tableNames]));
    return withBillTables(openTables, (billId) => {
      const bill = billsById.get(billId);
      if (bill) return tableNamesOfBill(bill);
      return takenNamesById.get(billId) ?? [];
    });
  }, [openTables, allTableBills, takenTables, tableNamesOfBill]);

  // Предстоящите + сега настанените (те заемат масите си) — без повторения.
  const reservations = useMemo(() => {
    if (!seatable) return [];
    const byId = new Map<number, Reservation>();
    for (const reservation of [...seatable.reservations, ...seatable.seatedNow]) {
      if (!byId.has(reservation.id)) byId.set(reservation.id, reservation);
    }
    return deriveReservations([...byId.values()], settleOpenTables, schemaTables, nowMs);
  }, [seatable, settleOpenTables, schemaTables, nowMs]);

  const snapshot = useMemo(
    () =>
      createFloorSnapshot({ reservations, openTables: floorOpenTables, momentMs: nowMs, nowMs, isAtNow: true }),
    [reservations, floorOpenTables, nowMs],
  );

  // Часът на следващата предстояща резервация след „сега" — за легендата.
  const nextReservationMs = useMemo(() => {
    const starts = reservations
      .filter((reservation) => reservation.status === "upcoming")
      .map(reservationStartMs)
      .filter((startMs) => startMs > nowMs);
    return starts.length > 0 ? Math.min(...starts) : null;
  }, [reservations, nowMs]);

  // Докато схемата още се зарежда, никоя сметка не е „извън схемата".
  const schemaLoaded = schemaQuery.data !== undefined;
  const temporary = useMemo(
    () => (schemaLoaded ? temporaryBills(sortedBills, index) : []),
    [schemaLoaded, sortedBills, index],
  );
  // Сивата лента — закритите без бон точно както ги показва списъкът.
  const nonFiscal = useMemo(() => nonFiscalBills(sortedBills), [sortedBills]);

  // Масите, чиято сметка се закрива (чака принтера) — по ключа на името; на
  // резервация с една сметка — всичките ѝ маси.
  const closingTableKeys = useMemo(
    () =>
      new Set(
        tableBills
          // Повторното закриване на нефискализирана е в сивата лента, не на масата.
          .filter((bill) => closingBillIds.has(bill.id) && !isClosedEmptyBill(bill))
          .flatMap((bill) => tableNamesOfBill(bill).map((tableName) => tableNameKey(tableName))),
      ),
    [tableBills, closingBillIds, tableNamesOfBill],
  );

  // Знакът горе вдясно на масата (процентът по сметката) — по показаните
  // отворени сметки; при няколко с едно име — първата по реда на списъка.
  const discountMarks = useMemo(() => {
    const marks = new Map<string, string | null>();
    for (const bill of tableBills) {
      if (isClosedEmptyBill(bill)) continue;
      const key = tableNameKey(bill.name);
      if (!marks.has(key)) marks.set(key, discountMarkOf(bill));
    }
    return marks;
  }, [tableBills]);
  const discountMarkOfTable = useCallback(
    (tableName: string) => discountMarks.get(tableNameKey(tableName)) ?? null,
    [discountMarks],
  );

  // Другите маси на „Една сметка" за резервация на няколко маси → сметката
  // (по ключа на името; само показаните сметки).
  const joinedBillIdsByKey = useMemo(() => {
    const byKey = new Map<string, number[]>();
    for (const bill of tableBills) {
      if (isClosedEmptyBill(bill)) continue;
      for (const tableName of tableNamesOfBill(bill).slice(1)) {
        const key = tableNameKey(tableName);
        byKey.set(key, [...(byKey.get(key) ?? []), bill.id]);
      }
    }
    return byKey;
  }, [tableBills, tableNamesOfBill]);

  /** Сметките, които държат масата, без да носят името ѝ: на настанената
   *  резервация, която я заема, и „Една сметка" за резервация на няколко
   *  маси (и без резервациите); празно — няма такива. */
  const linkedBillIdsOn = useCallback(
    (table: FloorTable) => {
      const state = snapshot.stateOf(table);
      const reservationIds =
        state.kind === "occupied" && state.reservation ? reservationBillIds(state.reservation) : [];
      const joinedIds = joinedBillIdsByKey.get(tableNameKey(table.name)) ?? [];
      return [...new Set([...reservationIds, ...joinedIds])];
    },
    [snapshot, joinedBillIdsByKey],
  );

  /** Чужда сметка на масата (по името, по другите маси на „Една сметка" или
   *  по резервацията ѝ), която гледащият НЕ вижда (без право — „Маса 2 е
   *  заета от Мария"); null — няма. */
  const hiddenOpenTableOf = useCallback(
    (table: FloorTable) => hiddenOpenTableFor(floorOpenTables, table.name, linkedBillIdsOn(table)),
    [floorOpenTables, linkedBillIdsOn],
  );

  // Обща номерация: масите, заети САМО от другите сервитьори — извън фокуса
  // (бледи); тези на сервитьора на фокус и свободните остават на фокус.
  const focusBillIds = useMemo(
    () =>
      sharedNumbering && focusWaiterId !== null
        ? new Set(
            tableBills
              .filter((bill) => billShownForWaiter(bill, focusWaiterId))
              .map((bill) => bill.id),
          )
        : null,
    [sharedNumbering, focusWaiterId, tableBills],
  );
  const { focusKeys, otherKeys, otherBillIds } = useMemo(() => {
    const focus = new Set<string>();
    const other = new Set<string>();
    const otherBills = new Set<number>();
    if (focusBillIds) {
      for (const openTable of floorOpenTables) {
        const key = tableNameKey(openTable.tableName);
        if (focusBillIds.has(openTable.billId)) focus.add(key);
        else {
          other.add(key);
          otherBills.add(openTable.billId);
        }
      }
    }
    return { focusKeys: focus, otherKeys: other, otherBillIds: otherBills };
  }, [focusBillIds, floorOpenTables]);
  const isOtherWaitersTable = useCallback(
    (table: FloorTable) => {
      if (!focusBillIds) return false;
      const key = tableNameKey(table.name);
      if (focusKeys.has(key)) return false;
      if (otherKeys.has(key)) return true;
      // Другата маса на настанена резервация — по сметките на резервацията.
      const billIds = linkedBillIdsOn(table);
      return billIds.some((billId) => otherBillIds.has(billId)) && !billIds.some((billId) => focusBillIds.has(billId));
    },
    [focusBillIds, focusKeys, otherKeys, otherBillIds, linkedBillIdsOn],
  );
  /** Плочка в лентите на друг сервитьор (обща номерация) — бледа. */
  const isOtherWaitersBill = useCallback(
    (bill: BillOut) => focusBillIds !== null && !focusBillIds.has(bill.id),
    [focusBillIds],
  );

  /** Клик по маса — пресни резервации и невидимите чужди маси. */
  const refreshFloor = useCallback(() => {
    if (readsReservations) void queryClient.invalidateQueries({ queryKey: SEATABLE_KEY });
    if (readsTakenTables) void queryClient.invalidateQueries({ queryKey: TAKEN_TABLES_KEY });
  }, [readsReservations, readsTakenTables, queryClient]);

  return {
    schemaQuery,
    regions,
    index,
    snapshot,
    temporary,
    nonFiscal,
    nextReservationMs,
    closingTableKeys,
    tableNamesOfBill,
    linkedBillIdsOn,
    discountMarkOfTable,
    hiddenOpenTableOf,
    isOtherWaitersTable,
    isOtherWaitersBill,
    refreshFloor,
  };
};

export type OrderingFloor = ReturnType<typeof useOrderingFloor>;
